<template>
  <header class="header">
    <div class="header-container">
      <div class="header-content">
        <!-- Logo 和品牌 -->
        <div class="header-left">
          <div class="logo">
            <PlayCircle class="logo-icon" />
            <h1 class="brand-name">智学云</h1>
          </div>
        </div>

        <!-- 导航菜单 -->
        <nav class="nav-menu">
          <router-link to="/" class="nav-link" exact>首页</router-link>
          <router-link to="/publicVideo" class="nav-link" v-permission="['public:video:list']">公区视频</router-link>
          <router-link to="/departmentVideo" class="nav-link" v-permission="['dept:video:list']">部门视频</router-link>
          <router-link to="/activityVideo" class="nav-link">集中活动</router-link>
          <router-link to="/training" class="nav-link">培训中心</router-link>
          <div class="nav-dropdown">
            <router-link to="/center/profile" class="nav-link nav-dropdown-trigger" :class="{ 'router-link-active': isCenterRoute }">
              <span>个人中心</span>
              <span class="nav-dropdown-arrow"></span>
            </router-link>
            <div class="nav-dropdown-menu">
              <router-link to="/center/profile" class="nav-dropdown-item">基本信息</router-link>
              <router-link to="/center/study" class="nav-dropdown-item">学习数据</router-link>
              <router-link to="/center/watch" class="nav-dropdown-item">观看记录</router-link>
              <router-link to="/center/note" class="nav-dropdown-item">我的笔记</router-link>
              <router-link to="/center/clock" class="nav-dropdown-item">打卡记录</router-link>
              <router-link to="/center/tasks" class="nav-dropdown-item">我的任务</router-link>
              <router-link to="/center/messages" class="nav-dropdown-item">消息通知</router-link>
            </div>
          </div>
        </nav>

        <!-- 右侧功能区 -->
        <div class="header-right">
          <!-- 搜索框 -->
          <div class="search-box">
            <Search class="search-icon" />
            <input
              type="text"
              placeholder="搜索视频..."
              v-model="searchKey"
              @keyup.enter="handleSearch"
              class="search-input"
            />
          </div>

          <!-- 通知 -->
          <div class="notice-wrap" ref="noticeWrap">
            <button class="notification-btn" @click.stop="toggleNoticePanel">
              <Bell />
              <span v-if="unreadCount > 0" class="notification-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
            </button>

            <div class="notice-panel" v-show="noticePanelVisible" @click.stop>
              <div class="notice-panel-header">
                <span class="notice-title">消息提醒</span>
                <button
                  class="notice-read-all-btn"
                  @click="handleReadAllNotices"
                  :disabled="noticeLoading || unreadCount === 0"
                >
                  一键已读
                </button>
              </div>

              <div class="notice-panel-body" v-loading="noticeLoading" element-loading-spinner="el-icon-loading">
                <div v-if="!noticeLoading && noticeList.length === 0" class="notice-empty">
                  暂无消息
                </div>
                <div
                  v-for="item in noticeList"
                  :key="item.id"
                  class="notice-item"
                  :class="{ unread: item.readFlag !== '1' }"
                  @click="handleNoticeClick(item)"
                >
                  <div class="notice-item-status">
                    <span v-if="item.readFlag !== '1'" class="notice-item-dot"></span>
                  </div>
                  <div class="notice-item-content">{{ item.content || '-' }}</div>
                  <div class="notice-item-bottom">
                    <span class="notice-item-time">{{ formatNoticeTime(item.createTime) }}</span>
                  </div>
                </div>
              </div>

              <div class="notice-panel-footer">
                <button class="notice-all-btn" @click="goNoticeCenter">查看全部消息</button>
              </div>
            </div>
          </div>

          <!-- 用户菜单 -->
          <div
            class="user-menu"
            @mouseenter="handleUserMenuEnter"
            @mouseleave="handleUserMenuLeave"
            @click.stop="toggleUserDropdown"
          >
            <div class="user-info">
              <div class="user-text">
                <span class="user-name">{{ userInfo.userName || '未登录' }}</span>
                <span class="user-status">
                  <span class="status-dot"></span> 在线
                </span>
              </div>
              <div class="user-avatar">
                <img :src="getUserAvatar" alt="用户头像" />
              </div>
            </div>
            <!-- 下拉菜单 -->
            <div class="user-dropdown" v-show="showUserDropdown">
              <router-link to="/center/profile" class="dropdown-item" @click.native.stop="closeUserDropdown">
                <User />
                基本信息
              </router-link>
              <router-link to="/center/study" class="dropdown-item" @click.native.stop="closeUserDropdown">
                <BarChart3 />
                学习数据
              </router-link>
              <router-link to="/center/watch" class="dropdown-item" @click.native.stop="closeUserDropdown">
                <History />
                观看记录
              </router-link>
              <router-link to="/center/note" class="dropdown-item" @click.native.stop="closeUserDropdown">
                <FileText />
                我的笔记
              </router-link>
              <router-link to="/center/clock" class="dropdown-item" @click.native.stop="closeUserDropdown">
                <CheckSquare2 />
                打卡记录
              </router-link>
              <router-link to="/center/tasks" class="dropdown-item" @click.native.stop="closeUserDropdown">
                <CheckSquare2 />
                我的任务
              </router-link>
              <router-link to="/center/messages" class="dropdown-item" @click.native.stop="closeUserDropdown">
                <Bell />
                消息通知
              </router-link>
              <hr class="dropdown-divider" />
              <a href="#" class="dropdown-item logout" @click.prevent="handleLogout">
                <LogOut />
                退出登录
              </a>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- 移动端汉堡菜单按钮（单独一行） -->
    <div class="mobile-menu-bar">
      <button class="mobile-hamburger-btn" @click="isMobileMenuOpen = true">
        <Menu />
      </button>
    </div>

    <!-- 侧边栏菜单 -->
    <div class="mobile-sidebar" :class="{ open: isMobileMenuOpen }">
      <div class="sidebar-header">
        <button class="sidebar-close-btn" @click="isMobileMenuOpen = false">
          <X />
        </button>
      </div>
      <nav class="sidebar-nav">
        <router-link to="/" class="sidebar-nav-link" exact @click="isMobileMenuOpen = false">首页</router-link>
        <router-link to="/publicVideo" class="sidebar-nav-link" v-permission="['public:video:list']" @click="isMobileMenuOpen = false">公区视频</router-link>
        <router-link to="/departmentVideo" class="sidebar-nav-link" v-permission="['dept:video:list']" @click="isMobileMenuOpen = false">部门视频</router-link>
        <router-link to="/activityVideo" class="sidebar-nav-link" @click="isMobileMenuOpen = false">集中活动</router-link>
        <router-link to="/training" class="sidebar-nav-link" @click="isMobileMenuOpen = false">培训中心</router-link>
        <router-link to="/center" class="sidebar-nav-link" @click="isMobileMenuOpen = false">个人中心</router-link>
      </nav>
    </div>

    <!-- 侧边栏遮罩 -->
    <div class="mobile-sidebar-overlay" v-if="isMobileMenuOpen" @click="isMobileMenuOpen = false"></div>
  </header>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  getMyUnreadNoticeCount,
  getMyUserNoticePage,
  readAllUserNotice,
  readUserNotice
} from '@/api/api'
import { buildTaskRouteFromNotice } from '@/utils/userNotice'
import { DEFAULT_AVATAR_PLACEHOLDER } from '@/utils/placeholders'

export default {
  name: 'CommonHeader',
  data() {
    return {
      searchKey: '',
      showUserDropdown: false,
      isMobileMenuOpen: false,
      noticePanelVisible: false,
      noticeLoading: false,
      noticeList: [],
      unreadCount: 0
    }
  },
  computed: {
    ...mapGetters(['getUserInfo']),
    userInfo() {
      return this.getUserInfo || JSON.parse(localStorage.getItem('user_info') || '{}')
    },
    getUserAvatar() {
      if (this.userInfo && this.userInfo.avatar) {
        // 后端已返回完整的 URL，无需再拼接 HOST
        return this.userInfo.avatar
      }
      return DEFAULT_AVATAR_PLACEHOLDER
    },
    isCenterRoute() {
      return this.$route.path.startsWith('/center')
    }
  },
  methods: {
    isMobileViewport() {
      return window.innerWidth <= 768
    },
    handleUserMenuEnter() {
      if (this.isMobileViewport()) return
      this.showUserDropdown = true
    },
    handleUserMenuLeave() {
      if (this.isMobileViewport()) return
      this.closeUserDropdown()
    },
    toggleUserDropdown() {
      if (!this.isMobileViewport()) {
        this.showUserDropdown = true
        return
      }
      this.showUserDropdown = !this.showUserDropdown
    },
    closeUserDropdown() {
      this.showUserDropdown = false
    },
    handleDocumentClick(event) {
      if (!this.$el || !event) return
      if (!this.$el.contains(event.target)) {
        this.closeUserDropdown()
      }
    },
    async handleLogout() {
      try {
        await this.$store.dispatch('logout')
        this.$message({
          message: '退出成功',
          type: 'success'
        })
        setTimeout(() => {
          this.$router.replace('/login').catch(() => {})
        }, 1000)
      } catch (error) {
        console.error('退出登录失败:', error)
        this.$message.error('退出登录失败，请重试')
      }
    },
    async loadUserInfo() {
      if (!this.getUserInfo && localStorage.getItem('user_token')) {
        try {
          await this.$store.dispatch('fetchUserInfo')
          // 获取用户权限信息
          await this.$store.dispatch('permission/getPermissions')
        } catch (error) {
          console.error('获取用户信息失败:', error)
          this.$message.error('登录已过期，请重新登录')
          setTimeout(() => {
            this.$store.dispatch('logout').finally(() => {
              this.$router.replace('/login').catch(() => {})
            })
          }, 1500)
        }
      }
    },
    async loadUnreadNoticeCount() {
      if (!localStorage.getItem('user_token')) {
        this.unreadCount = 0
        return
      }
      try {
        const res = await getMyUnreadNoticeCount()
        if (res.code === 1000) {
          this.unreadCount = Number((res.data && res.data.unreadCount) || 0)
        }
      } catch (error) {
        this.unreadCount = 0
      }
    },
    async loadNoticeList() {
      this.noticeLoading = true
      try {
        const res = await getMyUserNoticePage({
          pageNumber: 1,
          pageSize: 6
        })
        if (res.code === 1000) {
          this.noticeList = (res.data && res.data.records) || []
        } else {
          this.noticeList = []
        }
      } catch (error) {
        this.noticeList = []
      } finally {
        this.noticeLoading = false
      }
    },
    async handleReadAllNotices() {
      if (this.unreadCount <= 0) {
        return
      }
      const res = await readAllUserNotice()
      if (res.code === 1000) {
        this.$message.success('已全部标记为已读')
        this.unreadCount = 0
        await this.loadNoticeList()
      } else {
        this.$message.error(res.message || '操作失败，请稍后重试')
      }
    },
    async handleNoticeClick(notice) {
      if (!notice || !notice.id) {
        return
      }
      if (notice.readFlag !== '1') {
        const readRes = await readUserNotice({ id: notice.id })
        if (readRes.code === 1000) {
          notice.readFlag = '1'
          notice.readTime = new Date().toISOString()
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
      }
      this.noticePanelVisible = false
      const route = buildTaskRouteFromNotice(notice)
      route.query.noticeTs = `${Date.now()}`
      this.$router.push(route)
    },
    toggleNoticePanel() {
      this.noticePanelVisible = !this.noticePanelVisible
      if (this.noticePanelVisible) {
        this.loadNoticeList()
      }
    },
    handleClickOutside(event) {
      const wrap = this.$refs.noticeWrap
      if (!wrap) {
        return
      }
      if (!wrap.contains(event.target)) {
        this.noticePanelVisible = false
      }
    },
    goNoticeCenter() {
      this.noticePanelVisible = false
      this.$router.push('/center/messages')
    },
    formatNoticeTime(value) {
      if (!value) {
        return '-'
      }
      const date = new Date(String(value).replace(/-/g, '/'))
      if (Number.isNaN(date.getTime())) {
        return '-'
      }
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      return `${mm}-${dd} ${hh}:${mi}`
    },
    handleSearch() {
      if (this.searchKey) {
        this.$router.push({
          path: '/videoList',
          query: { keyword: this.searchKey }
        })
      }
    },
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
    }
  },
  mounted() {
    this.loadUserInfo()
    this.loadUnreadNoticeCount()
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('click', this.handleDocumentClick)
  }
}
</script>

<style scoped>
/* ==================== Header 样式 ==================== */
.header {
  position: sticky;
  top: 0;
  z-index: 1200;
  background-color: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  isolation: isolate;
}

.header-container {
  position: relative;
  z-index: 4;
  width: 100%;
  margin: 0 auto;
  padding: 0;
}

.header-content {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 20px;
  gap: 1.5rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 2.5rem;
  flex: 1 1 0;
  min-width: 0;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #1a71ff;
}
.logo-icon {
  width: 32px;
  height: 32px;
  color: #1a71ff;
}

.brand-name {
  color: #101318;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
}

.nav-menu {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 2rem;
}

.nav-link {
  text-decoration: none;
  color: #6b7280;
  font-size: 14px;
  font-family: '宋体', SimSun;
  font-weight: 600;
  transition: color 0.2s;
  white-space: nowrap;
}

.nav-link:hover {
  color: #1a71ff;
}

.nav-link.router-link-active {
  color: #1a71ff;
  font-weight: 600;
}

.nav-dropdown {
  position: relative;
  display: flex;
  align-items: center;
}

.nav-dropdown-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.nav-dropdown-arrow {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid currentColor;
  transition: transform 0.2s ease;
}

.nav-dropdown:hover .nav-dropdown-arrow {
  transform: rotate(180deg);
}

.nav-dropdown-menu {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 8px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: all 0.2s ease;
  z-index: 30;
}

.nav-dropdown-menu::before {
  content: '';
  position: absolute;
  top: -12px;
  left: 0;
  right: 0;
  height: 12px;
}

.nav-dropdown:hover .nav-dropdown-menu {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0);
}

.nav-dropdown-item {
  display: block;
  padding: 7px 12px;
  border-radius: 8px;
  color: #4b5563;
  text-decoration: none;
  font-size: 14px;
  font-family: '宋体', SimSun;
  font-weight: 400;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.nav-dropdown-item:hover,
.nav-dropdown-item.router-link-active {
  color: #1a71ff;
  background: #eff6ff;
}

.nav-dropdown-trigger.router-link-active {
  color: #1a71ff;
}

/* 响应式：隐藏小屏幕导航 */
@media (max-width: 768px) {
  .nav-menu {
    display: none;
  }
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
  flex: 1 1 0;
  min-width: 0;
}

.search-box {
  position: relative;
  width: clamp(180px, 24vw, 320px);
}

@media (max-width: 1280px) {
  .header-content {
    padding: 0 16px;
    gap: 1rem;
  }

  .nav-menu {
    gap: 1.25rem;
  }

  .search-box {
    width: 220px;
  }
}

@media (max-width: 992px) {
  .search-box {
    display: none;
  }

  .nav-menu {
    gap: 1rem;
  }
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  width: 20px;
  height: 20px;
  pointer-events: none;
  transition: color 0.2s;
}

.search-box:focus-within .search-icon {
  color: #1a71ff;
}

.search-input {
  width: 100%;
  padding: 8px 12px 8px 40px;
  border: none;
  border-radius: 9999px;
  background-color: #f0f2f5;
  color: #111827;
  font-size: 14px;
  font-family: '宋体', SimSun;
  outline: none;
  transition: all 0.2s;
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-input:focus {
  background-color: #ffffff;
  box-shadow: 0 0 0 2px rgba(26, 113, 255, 0.2);
}

/* 响应式：隐藏小屏幕搜索框 */
@media (max-width: 640px) {
  .search-box {
    display: none;
  }
}

.notification-btn {
  position: relative;
  padding: 8px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  border-radius: 9999px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.notification-btn svg {
  width: 20px;
  height: 20px;
}

.notification-btn:hover {
  color: #1a71ff;
  background-color: #f3f4f6;
}

.notification-badge {
  position: absolute;
  top: 0;
  right: -2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 9999px;
  background-color: #ef4444;
  color: #ffffff;
  font-size: 10px;
  line-height: 1;
  border: 2px solid #ffffff;
}

.notice-wrap {
  position: relative;
}

.notice-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 360px;
  background: linear-gradient(180deg, #f6f9ff 0%, #edf4ff 100%);
  border: 1px solid #d7e6ff;
  border-radius: 14px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.16);
  z-index: 120;
  overflow: hidden;
}

.notice-panel-header {
  height: 44px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(191, 214, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(10px);
}

.notice-title {
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
}

.notice-read-all-btn {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

.notice-read-all-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.notice-panel-body {
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: transparent;
}

.notice-empty {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 28px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.82);
}

.notice-item {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #dbe7ff;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.08);
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, transform 0.2s, box-shadow 0.2s;
}

.notice-item:hover {
  background: #ffffff;
  border-color: #bfd6ff;
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.12);
  transform: translateY(-1px);
}

.notice-item.unread {
  background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border-color: #bfd6ff;
}

.notice-item.unread::before {
  content: '';
  position: absolute;
  top: 14px;
  left: 12px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
}

.notice-item-status {
  display: none;
}

.notice-item-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2563eb;
  flex-shrink: 0;
}

.notice-item-time {
  color: #9ca3af;
  font-size: 11px;
  white-space: nowrap;
}

.notice-item-bottom {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.notice-item-content {
  color: #4b5563;
  font-size: 13px;
  line-height: 1.45;
  padding-left: 14px;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notice-panel-footer {
  height: 42px;
  border-top: 1px solid rgba(191, 214, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
}

.notice-all-btn {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 13px;
  cursor: pointer;
}

.user-menu {
  position: relative;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-left: 0.5rem;
  border-left: 1px solid #e5e7eb;
  cursor: pointer;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-text {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* 响应式：隐藏小屏幕用户文字 */
@media (max-width: 1024px) {
  .user-text {
    display: none;
  }
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  font-family: '宋体', SimSun;
}

.user-status {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-family: '宋体', SimSun;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background-color: #10b981;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.2s;
}

.user-menu:hover .user-avatar {
  border-color: #1a71ff;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  width: 192px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 4px 0;
  z-index: 20;
}

.user-dropdown::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  height: 8px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 6px 16px;
  font-size: 14px;
  color: #374151;
  text-decoration: none;
  transition: background-color 0.2s;
  gap: 12px;
  font-family: '宋体', SimSun;
  font-weight: 400;
}

.dropdown-item:hover {
  background-color: #f9fafb;
}

.dropdown-item.router-link-active {
  color: #1a71ff;
  background-color: #eff6ff;
}

.dropdown-item svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.dropdown-item.logout {
  color: #dc2626;
}

.dropdown-item.logout:hover {
  background-color: #fef2f2;
}

.dropdown-divider {
  margin: 4px 0;
  border: none;
  border-top: 1px solid #e5e7eb;
}

/* ==================== 移动端菜单 ==================== */
/* 汉堡菜单按钮栏 */
.mobile-menu-bar {
  display: none !important;
  background-color: #ffffff;
  padding: 0 16px;
  height: 56px;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  z-index: 2;
}

.mobile-hamburger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.mobile-hamburger-btn svg {
  width: 20px;
  height: 20px;
}

.mobile-hamburger-btn:active {
  color: #1a71ff;
}

/* 侧边栏菜单 */
.mobile-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 260px;
  height: 100vh;
  background-color: #ffffff;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  z-index: 999;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.mobile-sidebar.open {
  transform: translateX(0);
}

.sidebar-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  height: 56px;
}

.sidebar-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.sidebar-close-btn svg {
  width: 20px;
  height: 20px;
}

.sidebar-close-btn:active {
  color: #1a71ff;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  padding: 12px 0;
}

.sidebar-nav-link {
  display: block;
  padding: 16px 20px;
  color: #6b7280;
  text-decoration: none;
  font-size: 14px;
  font-family: '宋体', SimSun;
  font-weight: 600;
  border-left: 3px solid transparent;
  transition: all 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.sidebar-nav-link:hover {
  background-color: #f3f4f6;
  color: #1a71ff;
}

.sidebar-nav-link.router-link-active {
  background-color: #f0f2f4;
  color: #1a71ff;
  border-left-color: #1a71ff;
  font-weight: 600;
}

/* 侧边栏遮罩 */
.mobile-sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 手机端显示菜单 */
@media (max-width: 768px) {
  .mobile-menu-bar {
    display: flex !important;
  }

  .nav-menu {
    display: none;
  }

  .search-box {
    display: none;
  }

  .notification-btn {
    display: none;
  }

  .user-text {
    display: none;
  }

  .user-menu {
    border-left: none;
    padding-left: 0;
  }

  .user-dropdown {
    position: fixed;
    top: 64px;
    right: 12px;
    margin-top: 0;
    z-index: 1301;
  }

  .header-content {
    justify-content: space-between;
  }
}

@media (max-width: 480px) {
  .header-content {
    height: 56px;
    padding: 0 12px;
  }

  .header-left {
    gap: 0.5rem;
  }

  .logo-icon {
    font-size: 20px !important;
  }

  .brand-name {
    font-size: 14px;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
  }

  .user-dropdown {
    top: 56px;
    right: 8px;
    width: min(192px, calc(100vw - 16px));
  }

  .mobile-hamburger-btn {
    font-size: 18px;
    width: 36px;
    height: 36px;
  }

  .mobile-sidebar {
    width: 240px;
  }

  .sidebar-nav-link {
    padding: 14px 16px;
    font-size: 13px;
  }
}
</style>

